import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Save,
  Loader2,
  ArrowLeft,
  Upload,
  Eye,
  Plus,
  X,
  Trash2,
  Shield,
  Award,
  Users,
  CheckCircle,
  AlertTriangle,
  Droplet,
  Heart,
  FileText,
  Phone,
  MessageCircle,
  HelpCircle,
  Target,
  BookOpen,
  List,
  Download,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_OPTIONS = [
  { value: 'shield', label: 'Shield', icon: Shield },
  { value: 'award', label: 'Award', icon: Award },
  { value: 'users', label: 'Users', icon: Users },
  { value: 'check', label: 'Checkmark', icon: CheckCircle },
  { value: 'alert', label: 'Alert', icon: AlertTriangle },
  { value: 'droplet', label: 'Droplet', icon: Droplet },
  { value: 'heart', label: 'Heart', icon: Heart },
  { value: 'file', label: 'File', icon: FileText },
  { value: 'target', label: 'Target', icon: Target },
  { value: 'book', label: 'Book', icon: BookOpen },
];

const SafetyStandardsEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);
  const [uploading, setUploading] = useState({});

  const [config, setConfig] = useState({
    hero_title: '',
    hero_subtitle: '',
    trust_badges: [],
    standards_cards: [],
    milestones_title: '',
    milestones_subtitle: '',
    milestones: [],
    faq_title: '',
    faq_subtitle: '',
    faqs: [],
    safety_policy_pdf_url: null,
    eap_pdf_url: null,
    safeguarding_pdf_url: null,
    cta_title: '',
    cta_subtitle: '',
    cta_phone: '',
    cta_whatsapp: '',
    cta_enroll_text: '',
    cta_contact_text: '',
  });

  const updateConfig = useCallback((updater) => {
    setConfig(prev => typeof updater === 'function' ? updater(prev) : updater);
    setIsDirty(true);
    isDirtyRef.current = true;
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleNavigateBack = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Leave anyway?')) return;
    navigate(-1);
  };

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/safety-standards-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setIsDirty(false);
        isDirtyRef.current = false;
      }
    } catch (error) {
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/safety-standards-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(config),
      });
      if (res.ok) {
        toast.success('Safety standards saved');
        setIsDirty(false);
        isDirtyRef.current = false;
        fetchConfig();
      } else throw new Error();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file, fileType) => {
    setUploading(prev => ({ ...prev, [fileType]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', fileType);
      const res = await fetch(`${BACKEND_URL}/api/landing/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        const fieldMap = {
          safety_policy_pdf: 'safety_policy_pdf_url',
          eap_pdf: 'eap_pdf_url',
          safeguarding_pdf: 'safeguarding_pdf_url',
          safety_header: 'header_image_url',
        };
        updateConfig(prev => ({ ...prev, [fieldMap[fileType]]: data.image_url }));
        toast.success('File uploaded');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [fileType]: false }));
    }
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    // Already a full URL
    if (url.startsWith('http')) return url;
    // Build full URL using window.location.origin for proper browser navigation
    // This avoids React Router interception and ensures direct file access
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${url}`;
    }
    return `${BACKEND_URL}${url}`;
  };

  // Badge handlers
  const updateBadge = (idx, field, value) => {
    updateConfig(prev => ({
      ...prev,
      trust_badges: prev.trust_badges.map((b, i) => i === idx ? { ...b, [field]: value } : b),
    }));
  };

  // Card handlers
  const updateCard = (id, field, value) => {
    updateConfig(prev => ({
      ...prev,
      standards_cards: prev.standards_cards.map(c => c.id === id ? { ...c, [field]: value } : c),
    }));
  };

  const updateCardBullet = (cardId, bulletIdx, value) => {
    updateConfig(prev => ({
      ...prev,
      standards_cards: prev.standards_cards.map(c => {
        if (c.id !== cardId) return c;
        const bullets = [...(c.bullets || [])];
        bullets[bulletIdx] = value;
        return { ...c, bullets };
      }),
    }));
  };

  const addCardBullet = (cardId) => {
    updateConfig(prev => ({
      ...prev,
      standards_cards: prev.standards_cards.map(c => 
        c.id === cardId ? { ...c, bullets: [...(c.bullets || []), ''] } : c
      ),
    }));
  };

  const removeCardBullet = (cardId, bulletIdx) => {
    updateConfig(prev => ({
      ...prev,
      standards_cards: prev.standards_cards.map(c => {
        if (c.id !== cardId) return c;
        return { ...c, bullets: c.bullets.filter((_, i) => i !== bulletIdx) };
      }),
    }));
  };

  // Milestone handlers
  const updateMilestone = (idx, value) => {
    updateConfig(prev => ({
      ...prev,
      milestones: prev.milestones.map((m, i) => i === idx ? value : m),
    }));
  };

  const addMilestone = () => {
    updateConfig(prev => ({ ...prev, milestones: [...prev.milestones, ''] }));
  };

  const removeMilestone = (idx) => {
    updateConfig(prev => ({ ...prev, milestones: prev.milestones.filter((_, i) => i !== idx) }));
  };

  // FAQ handlers
  const addFaq = () => {
    if (config.faqs.length >= 8) return toast.error('Maximum 8 FAQs');
    updateConfig(prev => ({
      ...prev,
      faqs: [...prev.faqs, { id: `faq-${Date.now()}`, question: '', answer: '' }],
    }));
  };

  const updateFaq = (id, field, value) => {
    updateConfig(prev => ({
      ...prev,
      faqs: prev.faqs.map(f => f.id === id ? { ...f, [field]: value } : f),
    }));
  };

  const removeFaq = (id) => {
    updateConfig(prev => ({ ...prev, faqs: prev.faqs.filter(f => f.id !== id) }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto p-6" data-testid="safety-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleNavigateBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">Safety & Standards Editor</h1>
            <p className="text-slate-500">Edit safety content, FAQs, and downloads</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => window.open('/safety', '_blank')}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary" data-testid="save-safety-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Hero Section
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={config.hero_title} onChange={e => updateConfig(p => ({ ...p, hero_title: e.target.value }))} placeholder="Safety & Standards" />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Textarea value={config.hero_subtitle} onChange={e => updateConfig(p => ({ ...p, hero_subtitle: e.target.value }))} rows={2} />
          </div>
        </CardContent>
      </Card>

      {/* Trust Badges */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            Trust Badges (4)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {config.trust_badges.map((badge, idx) => (
              <div key={idx} className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div className="text-sm font-medium text-slate-700">Badge {idx + 1}</div>
                <div>
                  <Label className="text-xs">Label</Label>
                  <Input value={badge.label} onChange={e => updateBadge(idx, 'label', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Icon</Label>
                  <Select value={badge.icon} onValueChange={v => updateBadge(idx, 'icon', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map(opt => {
                        const Icon = opt.icon;
                        return (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2"><Icon className="w-4 h-4" />{opt.label}</div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Standards Cards */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-primary" />
            Standards Cards (4)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {config.standards_cards.map(card => {
            const CardIcon = ICON_OPTIONS.find(o => o.value === card.icon)?.icon || Shield;
            return (
              <div key={card.id} className="p-4 border rounded-lg space-y-4">
                <div className="flex items-center gap-3">
                  <CardIcon className="w-5 h-5 text-primary" />
                  <Input value={card.title} onChange={e => updateCard(card.id, 'title', e.target.value)} className="font-semibold" />
                  <Select value={card.icon} onValueChange={v => updateCard(card.id, 'icon', v)}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Bullet Points</Label>
                  {card.bullets?.map((bullet, bIdx) => (
                    <div key={bIdx} className="flex gap-2">
                      <Input value={bullet} onChange={e => updateCardBullet(card.id, bIdx, e.target.value)} />
                      <Button variant="ghost" size="icon" onClick={() => removeCardBullet(card.id, bIdx)} className="text-red-500 hover:text-red-700">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => addCardBullet(card.id)}>
                    <Plus className="w-4 h-4 mr-1" />Add Bullet
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Milestones */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <List className="w-5 h-5 text-primary" />
              Skill Progression Milestones
            </div>
            <Button variant="outline" size="sm" onClick={addMilestone}>
              <Plus className="w-4 h-4 mr-1" />Add
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Section Title</Label>
              <Input value={config.milestones_title} onChange={e => updateConfig(p => ({ ...p, milestones_title: e.target.value }))} />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input value={config.milestones_subtitle} onChange={e => updateConfig(p => ({ ...p, milestones_subtitle: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-2">
            {config.milestones.map((m, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">{idx + 1}</span>
                <Input value={m} onChange={e => updateMilestone(idx, e.target.value)} />
                <Button variant="ghost" size="icon" onClick={() => removeMilestone(idx)} className="text-red-500">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* FAQs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary" />
              FAQ ({config.faqs.length}/8)
            </div>
            <Button variant="outline" size="sm" onClick={addFaq} disabled={config.faqs.length >= 8}>
              <Plus className="w-4 h-4 mr-1" />Add
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Section Title</Label>
              <Input value={config.faq_title} onChange={e => updateConfig(p => ({ ...p, faq_title: e.target.value }))} />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input value={config.faq_subtitle} onChange={e => updateConfig(p => ({ ...p, faq_subtitle: e.target.value }))} />
            </div>
          </div>
          {config.faqs.map(faq => (
            <div key={faq.id} className="p-4 bg-slate-50 rounded-lg space-y-3 relative">
              <Button variant="ghost" size="icon" onClick={() => removeFaq(faq.id)} className="absolute top-2 right-2 text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
              <div>
                <Label className="text-xs">Question</Label>
                <Input value={faq.question} onChange={e => updateFaq(faq.id, 'question', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Answer</Label>
                <Textarea value={faq.answer} onChange={e => updateFaq(faq.id, 'answer', e.target.value)} rows={2} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Downloads */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Downloadable Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { key: 'safety_policy_pdf', label: 'Safety Policy PDF', field: 'safety_policy_pdf_url' },
              { key: 'eap_pdf', label: 'Emergency Action Plan', field: 'eap_pdf_url' },
              { key: 'safeguarding_pdf', label: 'Safeguarding Policy', field: 'safeguarding_pdf_url' },
            ].map(item => {
              const pdfUrl = config[item.field] ? getFileUrl(config[item.field]) : null;
              return (
                <div key={item.key} className="p-4 border rounded-lg">
                  <Label className="text-sm mb-2 block">{item.label}</Label>
                  <input
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    id={item.key}
                    onChange={e => e.target.files?.[0] && handleFileUpload(e.target.files[0], item.key)}
                  />
                  <label
                    htmlFor={item.key}
                    className="flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors"
                  >
                    {uploading[item.key] ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-slate-500">
                          {pdfUrl ? 'Replace' : 'Upload'} PDF
                        </span>
                      </>
                    )}
                  </label>
                  {pdfUrl && (
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t">
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          // Ensure native navigation, prevent React Router interception
                          e.stopPropagation();
                        }}
                        className="flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <Eye className="w-3 h-3" />
                        View PDF
                      </a>
                      <a
                        href={pdfUrl}
                        download={`${item.label.replace(/\s+/g, '_')}.pdf`}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        className="flex items-center gap-1 text-xs text-slate-600 hover:text-primary hover:underline"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* CTA */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-primary" />
            Call to Action
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Title</Label>
              <Input value={config.cta_title} onChange={e => updateConfig(p => ({ ...p, cta_title: e.target.value }))} />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input value={config.cta_subtitle} onChange={e => updateConfig(p => ({ ...p, cta_subtitle: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Phone</Label>
              <Input value={config.cta_phone} onChange={e => updateConfig(p => ({ ...p, cta_phone: e.target.value }))} placeholder="+268 7600 1234" />
            </div>
            <div>
              <Label>WhatsApp (no spaces)</Label>
              <Input value={config.cta_whatsapp} onChange={e => updateConfig(p => ({ ...p, cta_whatsapp: e.target.value }))} placeholder="+26876001234" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Enroll Button Text</Label>
              <Input value={config.cta_enroll_text} onChange={e => updateConfig(p => ({ ...p, cta_enroll_text: e.target.value }))} />
            </div>
            <div>
              <Label>Contact Button Text</Label>
              <Input value={config.cta_contact_text} onChange={e => updateConfig(p => ({ ...p, cta_contact_text: e.target.value }))} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bottom Save */}
      <div className="flex justify-end pb-8">
        <Button onClick={handleSave} disabled={saving} size="lg" className="bg-primary">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Changes
        </Button>
      </div>
    </div>
  );
};

export default SafetyStandardsEditor;
